import sha256 from 'crypto-js/sha256';
import { inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  Logger,
  ServiceProvider,
  Team,
  getFlavorDomain as getFlavorDomainCommon,
  getFlavorName as getFlavorNameCommon,
} from '@uploadcam/common';
import { FirebaseError } from 'firebase/app';
import { FunctionsError } from 'firebase/functions';
import { environment } from '../../environments/environment';
import { Environment } from '../../environments/types';
import * as Sentry from '@sentry/angular';

export function getLogger(tag: string): Logger {
  return inject(Logger).withTag(tag);
}

export function formatError(err: unknown): string {
  if (err instanceof Error) {
    const anyErr = err as any;
    const stack = `\n\nStack:\n${err.stack || ''}`;
    const details = anyErr.details
      ? `\n\nDetails:\n${JSON.stringify(anyErr.details, null, 2)}`
      : '';

    return `${err.name ?? 'Error'}: ${err.message ?? ''}${stack}${details}`;
  } else {
    return String(err);
  }
}

export function getFlavorName(): string {
  return getFlavorNameCommon(getFlavor());
}

export function getFlavorDomain(): string {
  return getFlavorDomainCommon(getFlavor());
}

export function getFlavor(): Environment['flavor'] {
  return environment.flavor;
}

export function getAlbumPublicFeedUrl(albumId: string): string {
  return `${environment.baseUrl}/a/${albumId}`;
}

export function getGalleryPublicUrl(slug: string): string {
  return `${environment.baseUrl}/g/${slug}`;
}

export function getDefaultTeamMainColor(): string {
  return getFlavor() === 'uc' ? '#1472f1' : '#1472f1';
}

export function getSentry(): typeof Sentry | undefined {
  if (isSentryEnabled()) {
    return Sentry;
  }

  return undefined;
}

export function isDev(): boolean {
  return environment.env === 'development';
}

export function isStaging(): boolean {
  return environment.env === 'staging';
}

export function isSentryEnabled(): boolean {
  return Boolean(environment.sentry?.dsn);
}

export function getSentryErrorsQueryUrl(query: string): string {
  if (!environment.sentry) {
    return 'https://sentry.io';
  }

  const dsnParts = environment.sentry.dsn.split('/');
  const project = dsnParts.pop();

  return `https://${environment.sentry.domain}/issues/?project=${project}&query=${encodeURIComponent(query)}`;
}

export function getSentryUserErrorsUrl(userId: string): string {
  return getSentryErrorsQueryUrl(`user:"id:${userId}"`);
}

export function imageLoaded(el: HTMLImageElement): boolean {
  return el.complete && el.naturalHeight !== 0;
}

export function hashPassword(teamId: string, password: string): string {
  // Hash the public password with a salt to prevent the use of precomputed
  // rainbow tables. The salt is public, which is acceptable because it forces
  // an attacker to generate a new rainbow table specific to this salt, adding
  // computational cost. Additionally, the hash is further protected by
  // including the `teamId`, which ensures that the hash is unique per team.
  // This approach is sufficient because the resource being protected is already
  // set to "public" visibility by the user in their storage provider (e.g.,
  // Google Drive), making the security requirements less critical.
  const salt = String.raw`~àVA^c*Kâ}á:èô¼-u-N.*ÖàÙÕõä¡ÿeÀîéí÷ðrõî²¡ÉRèìu"Ãw&77ÿ\YxÖí!`;

  return sha256(salt + teamId + password).toString();
}

export function isElementInViewport(el: Element): boolean {
  const rect = el.getBoundingClientRect();

  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

export function isAlbumDomainSharingEnabled(team: Team): boolean {
  return team.hasDomain() && team.folder.provider === ServiceProvider.Google;
}

export function isAlbumsLocationEnabled(team: Team): boolean {
  return isUploadCam();
}

export function isMapFilterEnabled(): boolean {
  // Map filter is only enabled for UploadCam
  return getFlavor() === 'uc';
}

export function isPublicAlbumsEnabled(team: Team): boolean {
  const teamIds: string[] = [
    'Ih40TrRw3Pw8hOgEpr9k', // Bitwise team
  ];

  return (
    team.folder.provider === ServiceProvider.Google &&
    (isDev() || isStaging() || teamIds.includes(team.id))
  );
}

export function isTeamGroupsEnabled(team: Team): boolean {
  return isSchoolCam() && team.folder.provider === ServiceProvider.Google;
}

export function isFirebaseFunctionsError(err: unknown): err is FunctionsError & { details: any } {
  return err instanceof FirebaseError && err.code.includes('functions/');
}

export function isUploadCam(): boolean {
  return getFlavor() === 'uc';
}

export function isSchoolCam(): boolean {
  return getFlavor() === 'sc';
}

export function showWip(): boolean {
  return environment.env !== 'production';
}

export function wait(timeMs: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(() => resolve(), timeMs);
  });
}

/**
 * Exponential backoff wait
 *
 * @param config.attempt Current attempt number (0-based)
 * @param config.baseDelayMs Base delay in milliseconds, this will be multiplied by 2^attempt
 * @param config.maxDelayMs Optional maximum delay in milliseconds. This allows initial exponential backoff, and linear backoff thereafter.
 */
export function waitExpBackoff(config: {
  attempt: number;
  baseDelayMs: number;
  maxDelayMs?: number;
}): Promise<void> {
  return wait(
    Math.min(
      Math.pow(2, Math.max(config.attempt, 0)) * config.baseDelayMs,
      config.maxDelayMs ?? Number.MAX_VALUE,
    ),
  );
}

export async function minWait(timeMs: number, promise: Promise<void>): Promise<void> {
  const start = Date.now();
  const waitDiff = () => wait(timeMs - (Date.now() - start));

  try {
    await promise;
    await waitDiff();
  } catch (err) {
    await waitDiff();
    throw err;
  }
}

export const normalizeCsvColumns = (header: string[]) =>
  header.map((col) =>
    col.toLowerCase().replace('[required]', '').replace('[read only]', '').trim().replace(' ', '_'),
  );

/**
 * Marks form controls as touched, and the empty ones as dirty.
 * This is needed to show validation errors for fields that only have a `required` validator.
 *
 * @see https://stackoverflow.com/a/73117410
 */
export function markAllAsTouched(form: FormGroup): void {
  form.markAllAsTouched();

  for (const key of Object.keys(form.controls)) {
    if (!form.controls[key].value) {
      form.controls[key].markAsDirty();
    }
  }
}

export function assertIsHtmlInputElement(el: unknown): asserts el is HTMLInputElement {
  if (!(el instanceof HTMLInputElement)) {
    throw new Error('Element is not an input');
  }
}

export function openNewTab(url: string): void {
  window.open(url, '_blank')?.focus();
}

export function downloadString(text: string, fileName: string): void {
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);

  // Create a temporary `<a>` element to trigger the download
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;

  // Click on the link
  document.body.appendChild(link); // Required for Firefox
  link.click();

  // Clean up by removing the link and revoking the URL
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function createTypedPostMessageFn<T>(): (event: T) => void {
  return (event: T) => {
    window.parent.postMessage(event);
  };
}

export class FriendlyError extends Error {
  constructor(
    /** Key for the friendly error message. Found in `error.friendly.<errorKey>` in the translations file. */
    public key: string,
    public innerError: unknown,
  ) {
    super(innerError instanceof Error ? innerError.message : String(innerError));
    Object.setPrototypeOf(this, FriendlyError.prototype);
    this.name = 'FriendlyError';
  }
}
